import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SummaryCards from "./SummaryCards";
import FilterTabs   from "./FilterTabs";
import PoolTable    from "./PoolTable";
import { getScgPoolStats, getScgPool } from "../../../../../api/scgPool";
import PageInfo from "../../../../../components/common/PageInfo";
import { exportScgPool } from "./exportScgPool";

const SCGPool = () => {
  const theme = useTheme();
  const t     = theme.custom;

  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [activeFilter, setActiveFilter] = useState("all");
  const [search,       setSearch]       = useState("");

  // Prevent concurrent inflight fetches from stacking
  const fetchingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    getScgPoolStats()
      .then((data) => { if (!cancelled) setStats(data); })
      .catch((err) => console.error("Failed to load SCG pool stats:", err))
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const fetchPool = useCallback(async () => {
    if (fetchingRef.current) return;          // block if already in flight
    fetchingRef.current = true;
    try {
      setLoading(true);
      setError(null);
      const data = await getScgPool({ filter: activeFilter });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load SCG pool:", err);
      setError("Failed to load pool data");
      setRows([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchPool();
  }, [fetchPool]);

  const filteredRows = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      row.fullName?.toLowerCase().includes(q)  ||
      row.levelCode?.toLowerCase().includes(q) ||
      row.levelName?.toLowerCase().includes(q) ||
      row.skills?.some((s) => s.toLowerCase().includes(q))
    );
  });

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      flex: 1, minHeight: 0, overflow: "hidden",
    }}>

      <Box sx={{
        padding: "20px 20px 16px",
        borderBottom: `1px solid ${t.border2}`,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "20px", fontWeight: 700,
          color: t.text, letterSpacing: "-0.01em",
          mb: "14px",
        }}>
          SCG Pool
        </Typography>
        <SummaryCards summary={statsLoading ? null : stats} />
      </Box>

      <PageInfo message="All billable associates currently in SCG Pool"/>

      <FilterTabs
        active={activeFilter}
        summary={stats}
        onSelect={(f) => { setActiveFilter(f); setSearch(""); }}
        search={search}
        onSearchChange={setSearch}
        onExport={() => exportScgPool(filteredRows, activeFilter)}
        exportDisabled={loading || filteredRows.length === 0}
      />

      {error && (
        <Box sx={{
          padding: "10px 20px", flexShrink: 0,
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px", color: "#c84040",
          borderBottom: `1px solid ${t.border}`,
        }}>
          {error}
        </Box>
      )}

      <PoolTable
        rows={filteredRows}
        loading={loading}
      />
    </Box>
  );
};

export default SCGPool;